// ---------------------------------------------------------------------------
// ChannelCardGrid - Grid layout with client-side filters + view-mode toggle.
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import { ChannelCard } from './ChannelCard';
import { ChannelsFilterRow } from './ChannelsFilterRow';
import type { ChannelCardGridProps } from './types';
import type { Channel } from '@/services/v11/channelsService';
import type { ChannelCardSparklineData } from './card/ChannelCardSparkline';

function buildChartKey(channel: Channel): string {
  return `${channel.username} (${channel.platform})`;
}

const EMPTY_SERIES: ChannelCardSparklineData = {
  interactions: [],
  posts: [],
  likes: [],
  comments: [],
};

export function ChannelCardGrid({
  channels,
  miniChartData,
  periodLabel,
  period,
  viewMode,
  onViewModeChange,
  includeOwnChannels = false,
  onIncludeOwnChannelsChange,
  isPeriodLoading,
  search,
  onSearchChange,
  platformFilter,
  onPlatformFilterChange,
  statusFilter,
  onStatusFilterChange,
}: ChannelCardGridProps) {
  const platforms = useMemo(() => {
    const set = new Set(channels.map(ch => ch.platform));
    return Array.from(set).sort();
  }, [channels]);

  const filtered = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    const sorted = [...channels].sort((a, b) => a.datasourceName.localeCompare(b.datasourceName));
    return sorted.filter(ch => {
      if (
        lowerSearch &&
        !ch.username.toLowerCase().includes(lowerSearch) &&
        !(ch.fullName ?? '').toLowerCase().includes(lowerSearch) &&
        !ch.datasourceName.toLowerCase().includes(lowerSearch)
      ) {
        return false;
      }
      if (platformFilter !== 'all' && ch.platform !== platformFilter) {
        return false;
      }
      if (statusFilter === 'active' && !ch.scrapingEnabled) return false;
      if (statusFilter === 'paused' && ch.scrapingEnabled) return false;
      return true;
    });
  }, [channels, search, platformFilter, statusFilter]);

  return (
    <div className="space-y-4">
      <ChannelsFilterRow
        search={search}
        onSearchChange={onSearchChange}
        platformFilter={platformFilter}
        onPlatformFilterChange={onPlatformFilterChange}
        platforms={platforms}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        includeOwnChannels={includeOwnChannels}
        onIncludeOwnChannelsChange={onIncludeOwnChannelsChange ?? (() => {})}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
      />

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-v11-border bg-v11-surface p-12 text-center">
          <p className="text-sm text-v11-text-muted">No channels match your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map(channel => (
            <ChannelCard
              key={channel.channelId}
              channel={channel}
              miniChartDataByMetric={miniChartData[buildChartKey(channel)] ?? EMPTY_SERIES}
              periodLabel={periodLabel}
              period={period}
              isPeriodLoading={isPeriodLoading}
            />
          ))}
        </div>
      )}
    </div>
  );
}
